<script setup lang="ts">
import { isAppUsing } from '@@/bits/flip'
import { __ } from '@@/bits/intl'
import { isRegistered } from '@@/bits/user_model'
import { AccessibilitySettingPreference } from '@@/enums'
import OzBox, { OzBoxColors } from '@@/library/v4/components/OzBox.vue'
import OzDivider, { OzDividerColorScheme } from '@@/library/v4/components/OzDivider.vue'
import OzLoadingCellsSkeleton from '@@/library/v4/components/OzLoadingCellsSkeleton.vue'
import OzLoadingTextSkeleton from '@@/library/v4/components/OzLoadingTextSkeleton.vue'
import type { OzRowDropdownRow } from '@@/library/v4/components/OzRowDropdown.vue'
import OzRowDropdown from '@@/library/v4/components/OzRowDropdown.vue'
import OzToggle from '@@/library/v4/components/OzToggle.vue'
import { AccessibilitySetting } from '@@/pinia/accessibility_settings_store'
import type { User, UserCamelCase } from '@@/types'
import { useAccessibilitySettings } from '@@/vuecomposables/useAccessibilitySettings'
import { computed, watch } from 'vue'

const props = withDefaults(
  defineProps<{
    user?: User | UserCamelCase
    updateType?: 'page' | 'global'
    settingsToShow?: Array<AccessibilitySetting>
    darkMode?: boolean | 'auto'
  }>(),
  {
    user: undefined,
    updateType: 'page',
    settingsToShow: () => [
      AccessibilitySetting.KeyboardShortcuts,
      AccessibilitySetting.AppMessagesAutoDismiss,
      AccessibilitySetting.HighContrastMode,
      AccessibilitySetting.ReducedMotion,
    ],
    darkMode: 'auto',
  },
)

const FEATURE_FLAGS = {
  [AccessibilitySetting.KeyboardShortcuts]: 'keyboardShortcutsSetting',
  [AccessibilitySetting.AppMessagesAutoDismiss]: 'autoDismissAppMessagesSetting',
  [AccessibilitySetting.HighContrastMode]: 'highContrastMode',
  [AccessibilitySetting.ReducedMotion]: 'reducedMotionSetting',
}

const user = computed(() => props.user)

const {
  isLoadingUserInfo,
  isKeyboardShortcutsEnabled,
  isAwaitingSaveIsKeyboardShortcutsEnabledResponse,
  isAppMessagesAutoDismissed,
  isAwaitingSaveIsAppMessagesAutoDismissedResponse,
  highContrastModePreference,
  isAwaitingSaveHighContrastModePreferenceResponse,
  reducedMotionPreference,
  isAwaitingSaveReducedMotionPreferenceResponse,
  toggleIsKeyboardShortcutsEnabled,
  toggleIsAppMessagesAutoDismissed,
  updateHighContrastModePreference,
  updateReducedMotionPreference,
  setStateUpdatesToBeLocallySaved,
} = useAccessibilitySettings(user)

watch(
  () => props.updateType,
  () => {
    if (user.value == null || !isRegistered(user.value)) {
      setStateUpdatesToBeLocallySaved(true)
    } else {
      setStateUpdatesToBeLocallySaved(props.updateType === 'page')
    }
  },
  {
    immediate: true,
  },
)

const shownSettings = computed<AccessibilitySetting[]>(() => {
  return props.settingsToShow.filter((setting) => {
    return isAppUsing(FEATURE_FLAGS[setting] as any)
  })
})

// #region Reduced motion dropdown
const reducedMotionSettingOptions: Record<
  AccessibilitySettingPreference,
  {
    shortTitle: string
    title: string
    description: string
  }
> = {
  [AccessibilitySettingPreference.System]: {
    shortTitle: __('System'),
    title: __('Follow system preferences'),
    description: __('Padlet will follow your motion preferences on your device.'),
  },
  [AccessibilitySettingPreference.Off]: {
    shortTitle: __('Off'),
    title: __('Off'),
    description: __('Animations, transitions, and GIFs will play normally.'),
  },
  [AccessibilitySettingPreference.On]: {
    shortTitle: __('On'),
    title: __('On'),
    description: __('Animations, transitions, and GIFs will be reduced.'),
  },
}
const currentReducedMotionSettingKey = computed(() => {
  if (reducedMotionPreference.value === AccessibilitySettingPreference.System || reducedMotionPreference.value === null)
    return AccessibilitySettingPreference.System
  return reducedMotionPreference.value === AccessibilitySettingPreference.On
    ? AccessibilitySettingPreference.On
    : AccessibilitySettingPreference.Off
})
const reducedMotionDropdownRows = computed<OzRowDropdownRow[]>(() =>
  Object.entries(reducedMotionSettingOptions).map(([key, value]) => ({
    htmlText: `<p class='text-body-small ${
      props.darkMode === 'auto'
        ? 'text-dark-text-100 dark:text-light-text-100'
        : props.darkMode === true
        ? 'text-light-text-100'
        : 'text-dark-text-100'
    }'>${value.title}</p><p class='text-body-extra-small ${
      props.darkMode === 'auto'
        ? 'text-dark-text-200 dark:text-light-text-200'
        : props.darkMode === true
        ? 'text-light-text-200'
        : 'text-dark-text-200'
    }'>${value.description}</p>`,
    rightIcon: currentReducedMotionSettingKey.value === key ? 'radio_selected' : 'radio_unselected',
    rightIconSize: 24,
    rightIconHighlighted: currentReducedMotionSettingKey.value === key,
    ariaChecked: String(currentReducedMotionSettingKey.value === key),
    eventPayload: key,
    testId: `reducedMotion${key}DropdownRow`,
  })),
)
const onReducedMotionOptionSelect = (_event: Event, eventPayload: AccessibilitySettingPreference): void => {
  updateReducedMotionPreference(eventPayload)
}
// #endregion

// #region /** High contrast mode dropdown */
const highContrastSettingOptions: Record<
  AccessibilitySettingPreference,
  {
    shortTitle: string
    title: string
    description: string
  }
> = {
  [AccessibilitySettingPreference.System]: {
    shortTitle: __('System'),
    title: __('Follow system preferences'),
    description: __('Padlet will follow your contrast preferences on your device.'),
  },
  [AccessibilitySettingPreference.Off]: {
    shortTitle: __('Off'),
    title: __('Off'),
    description: __('Standard contrast for text and buttons.'),
  },
  [AccessibilitySettingPreference.On]: {
    shortTitle: __('On'),
    title: __('On'),
    description: __('Higher contrast for better readability of text and buttons.'),
  },
}

const currentHighContrastSettingKey = computed(() => {
  if (
    highContrastModePreference.value === AccessibilitySettingPreference.System ||
    highContrastModePreference.value === null
  )
    return AccessibilitySettingPreference.System
  return highContrastModePreference.value === AccessibilitySettingPreference.On
    ? AccessibilitySettingPreference.On
    : AccessibilitySettingPreference.Off
})

const highContrastDropdownRows = computed<OzRowDropdownRow[]>(() =>
  Object.entries(highContrastSettingOptions).map(([key, value]) => ({
    htmlText: `<p class='text-body-small ${
      props.darkMode === 'auto'
        ? 'text-dark-text-100 dark:text-light-text-100'
        : props.darkMode === true
        ? 'text-light-text-100'
        : 'text-dark-text-100'
    }'>${value.title}</p><p class='text-body-extra-small ${
      props.darkMode === 'auto'
        ? 'text-dark-text-200 dark:text-light-text-200'
        : props.darkMode === true
        ? 'text-light-text-200'
        : 'text-dark-text-200'
    }'>${value.description}</p>`,
    rightIcon: currentHighContrastSettingKey.value === key ? 'radio_selected' : 'radio_unselected',
    rightIconSize: 24,
    rightIconHighlighted: currentHighContrastSettingKey.value === key,
    ariaChecked: String(currentHighContrastSettingKey.value === key),
    eventPayload: key,
    testId: `highContrast${key}DropdownRow`,
  })),
)

const onHighContrastOptionSelect = (_event: Event, eventPayload: AccessibilitySettingPreference): void => {
  updateHighContrastModePreference(eventPayload)
}
// #endregion
</script>

<template>
  <div>
    <div v-if="user ? isLoadingUserInfo : false">
      <OzLoadingTextSkeleton class="h-5 w-[115px] ms-2 mb-3" />
      <OzLoadingCellsSkeleton class="mb-8" :cells="2" />
    </div>
    <div v-else>
      <OzBox
        :color="OzBoxColors.Secondary"
        :dark-mode="darkMode"
        :class="['flex flex-col', 'px-4 py-1 tablet-portrait:px-5']"
      >
        <template v-for="(setting, index) in shownSettings">
          <template v-if="setting === AccessibilitySetting.KeyboardShortcuts">
            <div :key="setting" class="flex justify-between items-center py-5">
              <div class="flex-1 flex-col items-center me-4 mx-1">
                <span
                  id="keyboard-shortcuts-toggle"
                  :class="['text-body', 'tablet-portrait:me-2 tablet-portrait:w-44', 'flex-1']"
                  >{{ __('Keyboard shortcuts') }}</span
                >
                <p
                  :class="[
                    'text-body-small',
                    { 'text-dark-text-200 dark:text-light-text-200': darkMode === 'auto' },
                    { 'text-light-text-200': darkMode === true },
                    { 'text-dark-text-200': darkMode === false },
                  ]"
                >
                  {{
                    __(
                      'Enables single-key shortcuts: C to create a post, R to restart a slideshow, and F to go fullscreen on a slideshow.',
                    )
                  }}
                </p>
              </div>
              <OzToggle
                data-testid="keyboardShortcutsToggle"
                :value="isKeyboardShortcutsEnabled"
                aria-labelledby="keyboard-shortcuts-toggle"
                :dark-mode="darkMode"
                :disabled="isAwaitingSaveIsKeyboardShortcutsEnabledResponse"
                @input="toggleIsKeyboardShortcutsEnabled"
              />
            </div>
          </template>

          <template v-if="setting === AccessibilitySetting.AppMessagesAutoDismiss">
            <div :key="setting" class="flex justify-between items-center py-5">
              <div class="flex-1 flex-col items-center me-4 mx-1">
                <span
                  id="automatically-dismiss-app-messages-toggle"
                  :class="['text-body', 'tablet-portrait:me-2 tablet-portrait:w-44', 'flex-1']"
                  >{{ __('Automatically dismiss app messages') }}</span
                >
                <p
                  :class="[
                    'text-body-small',
                    { 'text-dark-text-200 dark:text-light-text-200': darkMode === 'auto' },
                    { 'text-light-text-200': darkMode === true },
                    { 'text-dark-text-200': darkMode === false },
                  ]"
                >
                  {{
                    __(
                      'If enabled, confirmation and warning messages disappear automatically after a short delay. If disabled, you will have to click these messages to dismiss them manually.',
                    )
                  }}
                </p>
              </div>
              <OzToggle
                data-testid="automaticallyDismissAppMessagesToggle"
                :value="isAppMessagesAutoDismissed"
                aria-labelledby="automatically-dismiss-app-messages-toggle"
                :disabled="isAwaitingSaveIsAppMessagesAutoDismissedResponse"
                :dark-mode="darkMode"
                @input="toggleIsAppMessagesAutoDismissed"
              />
            </div>
          </template>

          <template v-if="setting === AccessibilitySetting.HighContrastMode">
            <OzRowDropdown
              :key="setting"
              class="mx-1 py-5"
              :rows="highContrastDropdownRows"
              :field-label="__('High contrast mode')"
              :field-label-description="__('Adjust contrast for better readability of text and buttons.')"
              :dropdown-aria-label="__('High contrast mode setting dropdown')"
              :button-text="highContrastSettingOptions[currentHighContrastSettingKey].shortTitle"
              :button-test-id="'highContrastDropdownButton'"
              :dropdown-test-id="'highContrastDropdown'"
              :disabled="isAwaitingSaveHighContrastModePreferenceResponse"
              :dark-mode="darkMode"
              @select-row="onHighContrastOptionSelect"
            />
          </template>

          <template v-if="setting === AccessibilitySetting.ReducedMotion">
            <OzRowDropdown
              :key="setting"
              class="mx-1 py-5"
              :rows="reducedMotionDropdownRows"
              :field-label="__('Reduced motion')"
              :field-label-description="
                __('Reduce animations and transitions throughout the app. GIFs will not be autoplayed.')
              "
              :dropdown-aria-label="__('Reduced motion setting dropdown')"
              :button-text="reducedMotionSettingOptions[currentReducedMotionSettingKey].shortTitle"
              :button-test-id="'reducedMotionDropdownButton'"
              :dropdown-test-id="'reducedMotionDropdown'"
              :disabled="isAwaitingSaveReducedMotionPreferenceResponse"
              :dark-mode="darkMode"
              @select-row="onReducedMotionOptionSelect"
            />
          </template>
          <OzDivider
            v-if="index < shownSettings.length - 1"
            :key="'divider-' + index"
            :color-scheme="OzDividerColorScheme.Solid"
            :dark-mode="darkMode"
          />
        </template>
      </OzBox>
    </div>
  </div>
</template>
